/** @format */

/* eslint @typescript-eslint/prefer-nullish-coalescing: "warn" -- Legacy code: do not use as an example. */
import isPropValid from '@emotion/is-prop-valid';
import { Heading } from '@mpb-com/feather/components/atoms/heading';
import { HeadingSize } from '@mpb-com/feather/components/atoms/heading/types';
import { PillLabel } from '@mpb-com/feather/components/atoms/pill-label';
import {
    Skeleton,
    SkeletonVariant,
} from '@mpb-com/feather/components/atoms/skeleton';
import { useTranslation } from 'next-i18next';
import React, { FunctionComponent, ReactElement, ReactNode } from 'react';
import styled from 'styled-components';

import { WrappedLink } from '@toucan/client/global/nextjs';

import { LinkVariant } from '@toucan/client/shared/components/link';

import { styles } from './story-card.styles';

// Touch and Change
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions -- Legacy code: do not use as an example.
export interface StoryCardReadMoreLinkDetails {
    href: string;
    templatePath: string;
}

// Touch and Change
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions -- Legacy code: do not use as an example.
export interface StoryCardProps {
    /**
     * Title for the card
     */
    title?: ReactNode;

    /**
     * Teaser text for the card
     */
    teaser?: ReactNode;

    /**
     * Thumbnail image for the card
     */
    thumbnail?: ReactNode;

    /**
     * Whether the component is loading
     */
    // eslint-disable-next-line @typescript-eslint/naming-convention -- Legacy code: do not use as an example.
    loading?: boolean;

    /**
     * An optional content type to be displayed in the story card
     */
    contentType?: string;

    /**
     * Published at date
     */
    publishedAt?: string;

    /**
     * Optional read more link
     */
    readMoreLink?: StoryCardReadMoreLinkDetails;

    /**
     * Optional label override
     */
    readMoreLabel?: string;

    /**
     * Selection ID for test purposes.
     */
    'data-testid'?: string;

    /**
     * Custom className
     */
    className?: string;

    'data-id'?: string;
}

type OptionalWrappedLinkProps = {
    children: ReactElement;
    link?: StoryCardReadMoreLinkDetails;
    role?: string;
    className?: string;
    'data-id'?: string;
};

/**
 * Return content wrapped in a read more link, if the read
 * more link has been provided
 */
const OptionalWrappedLink = ({
    children,
    role = 'link',
    className,
    link,
    'data-id': dataId = 'optional-wrapped-link',
}: OptionalWrappedLinkProps) => {
    if (!link) {
        return children;
    }

    return (
        <WrappedLink
            variant={LinkVariant.CUSTOM}
            role={role}
            href={link.templatePath}
            as={link.href}
            tabIndex={-1}
            className={className}
            data-id={dataId}
        >
            {children}
        </WrappedLink>
    );
};

const StyledStoryCard = styled('article').withConfig({
    shouldForwardProp: (prop) => isPropValid(prop),
})`
    ${styles}
`;

// eslint-disable-next-line @mpb-com/mpb/single-styled-component -- Legacy code: do not use as an example.
const StyledStoryHeading = styled(Heading)``;

export const StoryCard: FunctionComponent<StoryCardProps> = ({
    title,
    teaser,
    thumbnail,
    loading,
    contentType,
    readMoreLink,
    readMoreLabel,
    publishedAt,
    'data-id': dataId = 'story-card',
    ...otherProps
}: StoryCardProps) => {
    const { t } = useTranslation();
    const label = readMoreLabel || t('Read more', { context: 'storyCard' });

    return (
        <StyledStoryCard {...otherProps} data-id={dataId}>
            {loading ? (
                <Skeleton
                    variant={SkeletonVariant.BLOCK}
                    className="story-card-thumbnail-skeleton"
                    data-testid="story-card__thumbnail-skeleton"
                    data-id={`${dataId}__thumbnail-skeleton`}
                />
            ) : (
                <OptionalWrappedLink
                    link={readMoreLink}
                    className="story-card-thumbnail-link"
                    data-id={`${dataId}__thumbnail-link`}
                >
                    <div
                        className="story-card-thumbnail"
                        data-testid="story-card__thumbnail"
                        data-id={`${dataId}__thumbnail`}
                    >
                        {thumbnail}
                        {/* eslint-disable-next-line react/jsx-no-leaked-render -- Legacy code: do not use as an example. */}
                        {contentType && (
                            <PillLabel
                                className="story-card-content-type"
                                data-testid="story-card__content-type"
                                data-id={`${dataId}__content-type`}
                            >
                                {contentType}
                            </PillLabel>
                        )}
                    </div>
                </OptionalWrappedLink>
            )}
            <div className="story-card-content" data-id={`${dataId}__content`}>
                {loading ? (
                    <Skeleton
                        variant={SkeletonVariant.TEXT}
                        className="story-card-date-skeleton"
                        data-testid="story-card__date-skeleton"
                        data-id={`${dataId}__date-skeleton`}
                        lineHeight={0.8}
                    />
                ) : (
                    publishedAt && (
                        <div
                            className="story-card-date"
                            data-testid="story-card__date"
                            data-id={`${dataId}__date`}
                        >
                            {publishedAt}
                        </div>
                    )
                )}

                {loading ? (
                    <Skeleton
                        variant={SkeletonVariant.TEXT}
                        className="story-card-title-skeleton"
                        data-testid="story-card__title-skeleton"
                        data-id={`${dataId}__title-skeleton`}
                    />
                ) : (
                    <OptionalWrappedLink
                        link={readMoreLink}
                        role="menuitem"
                        className="story-card-title"
                        data-id={`${dataId}__title-link`}
                    >
                        <StyledStoryHeading
                            size={HeadingSize.H3}
                            data-testid="story-card__title"
                            data-id={`${dataId}__title`}
                        >
                            {title}
                        </StyledStoryHeading>
                    </OptionalWrappedLink>
                )}

                {loading ? (
                    <Skeleton
                        variant={SkeletonVariant.TEXT}
                        className="story-card-teaser-skeleton"
                        data-testid="story-card__teaser-skeleton"
                        data-id={`${dataId}__teaser-skeleton`}
                        lineCount={3}
                    />
                ) : (
                    <div
                        className="story-card-teaser"
                        data-testid="story-card__teaser"
                        data-id={`${dataId}__teaser`}
                    >
                        {teaser}
                    </div>
                )}
            </div>
            <div className="story-card-footer" data-id={`${dataId}__footer`}>
                {loading ? (
                    <Skeleton
                        variant={SkeletonVariant.TEXT}
                        className="story-card-footer-skeleton"
                        data-testid="story-card__footer-skeleton"
                        data-id={`${dataId}__footer-skeleton`}
                    />
                ) : (
                    readMoreLink && (
                        <WrappedLink
                            href={readMoreLink.templatePath}
                            as={readMoreLink.href}
                            data-testid="story-card__footer"
                            data-id={`${dataId}__footer`}
                        >
                            {label}
                        </WrappedLink>
                    )
                )}
            </div>
        </StyledStoryCard>
    );
};
